import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Reports } from '../../../../../../modules/API/Reports/reports'
import { IReportListItem } from '../../../../../../modules/API/Reports/models/reportList'
import { IDataQ4, IMetaDataQ4, IReportQ4Response } from '../../../../../../modules/API/Reports/models/q4'
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications'
import useExportToExcel from '../../../../../../generic/hooks/useExportToExcel'
import useDataFetcher from '../../../knisot/hooks/useFetchData'
import { buildQ4ExcelColumns, filterData } from '../helpers/utils'
import { formatQ4Summary } from '../helpers/q4Report'
import { toReportRows } from '../../shared/helpers/reportRows'
import { ReportDateRange, ReportRow } from '../../shared/types/row'
import { IProcedures } from '../../../../../../../store/reducers/procedures'
import { RootState } from '../../../../../../../store/reducers/rootReducer'
import {
  SELECT_DATE_PROCEDURES_Q4,
  SELECT_REPORT_PROCEDURES_Q4,
} from '../../../../../../../store/actions/actionsTypes'
import { Q4SummaryValues } from '../types/row'

export interface Q4FilterFormProps {
  setStartDate: Dispatch<SetStateAction<string>>
  setEndDate: Dispatch<SetStateAction<string>>
  reportList: IReportListItem[] | null
  handleSave: (RepID: number) => void
  handleDate: (e: [Date | null, Date | null]) => void
  setDefaultDate: IProcedures['Q4']['date']
  cashReport: IReportListItem | null
  handleDispatchReport: (item: IReportListItem) => void
}

export interface UseQ4ReportReturn {
  /** null — not loaded yet */
  report: IReportQ4Response | null
  metaData: IMetaDataQ4 | null
  /** filtered by search */
  rows: ReportRow[]
  totalCount: number
  /** The loaded report has rows (search does not affect it). */
  hasData: boolean
  isLoading: boolean
  summary: Q4SummaryValues
  searchQuery: string
  handleSearch: (query: string) => void
  isFilterOpen: boolean
  toggleFilter: () => void
  filterFormProps: Q4FilterFormProps
  handleExport: () => void
}

const LOAD_ERROR_MESSAGE = 'שגיאה בטעינת הדוח'
const REPORT_LIST_TYPE = '2'

export const useQ4Report = (): UseQ4ReportReturn => {
  const dispatch = useDispatch()
  const storedDate = useSelector((state: RootState) => state.procedures.Q4.date)
  const storedReport = useSelector((state: RootState) => state.procedures.Q4.selectedReport)

  const { data: reportList } = useDataFetcher<IReportListItem[]>(
    Reports.getReportList,
    REPORT_LIST_TYPE,
  )

  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  // null — not loaded yet.
  const [report, setReport] = useState<IReportQ4Response | null>(null)
  // Dates of the last successful request — the summary shows what the table holds, not the live picker.
  const [appliedRange, setAppliedRange] = useState<ReportDateRange | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(true)
  const [searchQuery, setSearchQuery] = useState<string>('')

  const toggleFilter = useCallback(() => setIsFilterOpen((prev) => !prev), [])

  const handleSave = useCallback(
    async (RepID: number) => {
      setIsFilterOpen(false)
      setIsLoading(true)
      try {
        const res = await Reports.getReportQ4({ FromDate: startDate, ToDate: endDate, RepID })
        setReport(res)
        setAppliedRange({ from: startDate, to: endDate })
      } catch {
        setIsFilterOpen(true)
        handleNotifications(LOAD_ERROR_MESSAGE, { type: 'error' })
      } finally {
        setIsLoading(false)
      }
    },
    [startDate, endDate],
  )

  const handleDate = useCallback(
    (e: [Date | null, Date | null]) => {
      dispatch({ type: SELECT_DATE_PROCEDURES_Q4, payloadDate: e })
    },
    [dispatch],
  )

  const handleDispatchReport = useCallback(
    (item: IReportListItem) => {
      dispatch({ type: SELECT_REPORT_PROCEDURES_Q4, report: item })
    },
    [dispatch],
  )

  const filteredData = useMemo<IDataQ4[]>(
    () => (report ? filterData(report.Data, searchQuery) : []),
    [report, searchQuery],
  )

  const rows = useMemo(() => toReportRows(filteredData), [filteredData])

  const summary = useMemo(
    () => formatQ4Summary(report?.Summary ?? null, appliedRange),
    [report, appliedRange],
  )

  const excelColumns = useMemo(
    () => (report ? buildQ4ExcelColumns(report.MetaData) : []),
    [report],
  )
  const { handleExport: exportToExcel } = useExportToExcel<IDataQ4>({
    columns: excelColumns,
    fileName: 'q4',
    rtl: true,
  })
  const handleExport = useCallback(() => exportToExcel(filteredData), [exportToExcel, filteredData])

  const filterFormProps = useMemo<Q4FilterFormProps>(
    () => ({
      setStartDate,
      setEndDate,
      reportList,
      handleSave,
      handleDate,
      setDefaultDate: storedDate,
      cashReport: storedReport,
      handleDispatchReport,
    }),
    [reportList, handleSave, handleDate, storedDate, storedReport, handleDispatchReport],
  )

  return {
    report,
    metaData: report?.MetaData ?? null,
    rows,
    totalCount: rows.length,
    hasData: !!report?.Data.length,
    isLoading,
    summary,
    searchQuery,
    handleSearch: setSearchQuery,
    isFilterOpen,
    toggleFilter,
    filterFormProps,
    handleExport,
  }
}
